import React from 'react';
import LocationSearch from './LocationSearch';

/**
 * Format raw digits as dd/mm/yyyy. User types 17101977 → 17/10/1977.
 * Separators are inserted automatically; user never types / manually.
 */
function formatDateForDisplay(digits) {
  const d = String(digits || '').replace(/\D/g, '');
  if (d.length <= 2) return d;
  if (d.length <= 4) return `${d.slice(0, 2)}/${d.slice(2)}`;
  return `${d.slice(0, 2)}/${d.slice(2, 4)}/${d.slice(4, 8)}`;
}

/**
 * Format raw digits as hh:mm. User types 1345 → 13:45.
 * Colon is inserted automatically; user never types : manually.
 */
function formatTimeForDisplay(digits) {
  const d = String(digits || '').replace(/\D/g, '');
  if (d.length <= 2) return d;
  return `${d.slice(0, 2)}:${d.slice(2, 4)}`;
}

/**
 * Parse user date input to API format (YYYY-MM-DD).
 * Accepts: dd/mm/yyyy, dd-mm-yyyy, yyyy-mm-dd.
 */
export function parseDateInput(raw) {
  const s = String(raw || '').trim();
  if (!s) return null;
  // yyyy-mm-dd (ISO)
  const iso = /^(\d{4})-(\d{1,2})-(\d{1,2})$/.exec(s);
  if (iso) {
    const [, y, m, d] = iso;
    const yy = parseInt(y, 10);
    const mm = parseInt(m, 10);
    const dd = parseInt(d, 10);
    if (yy >= 1900 && yy <= 2100 && mm >= 1 && mm <= 12 && dd >= 1 && dd <= 31) {
      return `${yy}-${String(mm).padStart(2, '0')}-${String(dd).padStart(2, '0')}`;
    }
  }
  // dd/mm/yyyy or dd-mm-yyyy
  const dmy = /^(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{4})$/.exec(s);
  if (dmy) {
    const [, d, m, y] = dmy;
    const dd = parseInt(d, 10);
    const mm = parseInt(m, 10);
    const yy = parseInt(y, 10);
    if (yy >= 1900 && yy <= 2100 && mm >= 1 && mm <= 12 && dd >= 1 && dd <= 31) {
      return `${yy}-${String(mm).padStart(2, '0')}-${String(dd).padStart(2, '0')}`;
    }
  }
  return null;
}

/**
 * Parse user time input to API format (HH:mm).
 * Accepts: hh:mm, hh:mm:ss, h.mm.
 */
export function parseTimeInput(raw) {
  const s = String(raw || '').trim();
  if (!s) return null;
  // hh:mm or hh:mm:ss
  const hm = /^(\d{1,2}):(\d{2})(?::(\d{2}))?$/.exec(s);
  if (hm) {
    const h = parseInt(hm[1], 10);
    const m = parseInt(hm[2], 10);
    if (h >= 0 && h <= 23 && m >= 0 && m <= 59) {
      return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
    }
  }
  // h.mm (e.g. 9.30)
  const hdot = /^(\d{1,2})\.(\d{2})$/.exec(s);
  if (hdot) {
    const h = parseInt(hdot[1], 10);
    const m = parseInt(hdot[2], 10);
    if (h >= 0 && h <= 23 && m >= 0 && m <= 59) {
      return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
    }
  }
  return null;
}

const BirthChartForm = ({
  values,
  errors,
  onChange,
  onSubmit,
  generating,
  selectedLocation,
  onLocationSelect,
}) => (
  <form className="chart-form card" onSubmit={onSubmit}>
    <h2>Birth details</h2>
    <p className="chart-help">
      Enter your birth data to generate your full natal chart. You do not need to understand astrology yet.
    </p>

    <div className="chart-grid">
      <div className="form-group">
        <label htmlFor="chart-date">Date of birth</label>
        <input
          id="chart-date"
          className="form-control chart-date-input"
          type="text"
          inputMode="numeric"
          autoComplete="bday"
          placeholder="dd/mm/yyyy"
          value={values.birthDate}
          onChange={(e) => {
            const formatted = formatDateForDisplay(e.target.value);
            onChange('birthDate', formatted);
          }}
          required
          aria-describedby="chart-date-hint"
        />
        <p id="chart-date-hint" className="chart-input-hint" aria-live="polite">
          Format: dd/mm/yyyy
        </p>
        {errors.birthDate && <p className="chart-error-text">{errors.birthDate}</p>}
      </div>

      <div className="form-group">
        <label htmlFor="chart-time">Time of birth</label>
        <input
          id="chart-time"
          className="form-control chart-time-input"
          type="text"
          inputMode="numeric"
          placeholder="hh:mm"
          value={values.birthTime}
          onChange={(e) => {
            const formatted = formatTimeForDisplay(e.target.value);
            onChange('birthTime', formatted);
          }}
          disabled={values.timeUnknown}
          required={!values.timeUnknown}
          aria-describedby="chart-time-hint"
        />
        <p id="chart-time-hint" className="chart-input-hint" aria-live="polite">
          Format: hh:mm
        </p>
        {errors.birthTime && <p className="chart-error-text">{errors.birthTime}</p>}
      </div>
    </div>

    <label className="chart-checkbox">
      <input
        type="checkbox"
        checked={values.timeUnknown}
        onChange={(event) => onChange('timeUnknown', event.target.checked)}
      />
      <span>Time unknown</span>
    </label>

    {values.timeUnknown && (
      <p className="chart-help">
        Time unknown mode uses 12:00 as a neutral placeholder. Houses, Ascendant, and exact Moon position may be less reliable.
      </p>
    )}

    <LocationSearch
      country={values.country}
      onCountryChange={(value) => onChange('country', value)}
      query={values.locationQuery}
      onQueryChange={(value) => onChange('locationQuery', value)}
      selectedLocation={selectedLocation}
      onLocationSelect={onLocationSelect}
      error={errors.location}
    />

    <button className="btn btn-primary chart-generate-btn" type="submit" disabled={generating}>
      {generating ? 'Generating...' : 'Generate chart'}
    </button>
  </form>
);

export default BirthChartForm;
